import React, { useEffect, useState } from "react";
import { auth, db } from "./firebase";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { updatePassword , reauthenticateWithCredential, EmailAuthProvider } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { downloadFile } from "./utils/downloadFile";

function Profile() {
    const [userData, setUserData] = useState(null);
    const [activityLogs, setActivityLogs] = useState([]);
    const [scrubLogs, setScrubLogs] = useState([]);
    const [expandedId, setExpandedId] = useState(null);
    const [oldPassword, setOldPassword] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPasswordForm, setShowPasswordForm] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [success, setSuccess] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [currentPassword, setCurrentPassword] = useState("");
    const [showScrubHistory, setShowScrubHistory] = useState(true);
    const [showPaymentHistory, setShowPaymentHistory] = useState(true);
    const [token, setToken] = useState("");

    const user = auth.currentUser;
    const navigate = useNavigate();
    const latestScrubLog = scrubLogs[0];
    const latestPaymentLog = activityLogs[0];

    useEffect(() => {
        const fetchUserData = async () => {
            if (!user) {
                navigate("/login");
                return;
            }

            const userRef = doc(db, "users", user.uid);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
                setUserData(userSnap.data());
            }
        };

        const fetchActivityLogs = async () => {
            if (!user) return;

            const q = query(collection(db, "user_activity"), where("uid", "==", user.uid));
            const snapshot = await getDocs(q);
            //18.15.0
            const sorted = snapshot.docs
                .map(doc => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        ...data,
                        date: data.last_purchase_date?.toDate().toLocaleString()
                    };
                })
                .filter(log => !!log.purchase_type)
                .sort((a, b) => b.last_purchase_date?.toDate() - a.last_purchase_date?.toDate());

            setActivityLogs(sorted);
        };

        const fetchScrubLogs = async () => {
            if (!user) return;

            const q = query(collection(db, "scrub"), where("uid", "==", user.uid));
            const snapshot = await getDocs(q);
            //18.15.0
            const sorted = snapshot.docs
                .map(doc => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        ...data,
                        date: data.scrub_date?.toDate().toLocaleString()
                    };
                })
                //.filter(log => !!log.purchase_type)
                .sort((a, b) => b.scrub_date?.toDate() - a.scrub_date?.toDate());

            setScrubLogs(sorted);
        };

        fetchUserData();
        fetchActivityLogs();
        fetchScrubLogs();

        if (user) {
            user.getIdToken().then(setToken);
        }
    }, [navigate, user]);

    const isEmailPasswordUser = user?.providerData.some(p => p.providerId === "password");

    const toggleExpand = (id) => {
        setExpandedId(expandedId === id ? null : id);
    };

    const handlePasswordUpdate = async () => {
        setError("");
        setSuccess("");
        setLoading(true);
        const user = auth.currentUser;

        try {
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential); // 🔐 Re-authenticate
            await updatePassword(user, newPassword); // 🔑 Update
            setSuccess("✅ Password updated successfully!");
        } catch (err) {
            console.error(err);
            if (err.code === "auth/wrong-password") {
                setError("❌ The current password is incorrect.");
            } else if (err.code === "auth/requires-recent-login") {
                setError("❌ Please log in again to update your password.");
            } else {
                setError(`❌ ${err.message}`);
            }
        } finally {
            setLoading(false);
            setShowConfirm(false);
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
        }
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        if (newPassword !== confirmPassword) {
            setError("❌ New passwords do not match.");
            return;
        }

        if (newPassword.length < 6) {
            setError("❌ Password must be at least 6 characters long.");
            return;
        }

        setShowConfirm(true); // 👁️ Show confirmation modal
    };

    if (!userData) return <p style={{ textAlign: "center" }}>Loading profile...</p>;

    return (
        <div className="profile-container">
            {/* 1. Profile Info */}
            <section className="profile-section">
                <h2>👤 Profile Information</h2>
                <p><strong>Email :</strong> {userData.email}</p>
                {/*<p><strong>Type :</strong> {userData.type}</p>*/}
                <p><strong>Balance :</strong> 💰 {userData.balance} credits</p>
                <p><strong>Registered :</strong> {userData.register_date?.toDate().toLocaleString()}</p>
            </section>

            {/* 2. Password Reset */}
            {isEmailPasswordUser && (
                <section className="profile-section">
                    <h2>🔐 Change Password</h2>
                    <label>
                        <input
                            type="checkbox"
                            checked={showPasswordForm}
                            onChange={(e) => setShowPasswordForm(e.target.checked)}
                            style={{ marginRight: "10px" }}
                        />
                        Show password reset form
                    </label>

                    {showPasswordForm && (
                        <>
                            <form onSubmit={onSubmit}>
                                <input
                                    type="password"
                                    placeholder="Current Password"
                                    className="login-input"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    required
                                />
                                <input
                                    type="password"
                                    placeholder="New Password"
                                    className="login-input"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    required
                                />
                                <input
                                    type="password"
                                    placeholder="Confirm New Password"
                                    className="login-input"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                />
                                <button type="submit" className="login-submit-btn" disabled={loading}>
                                    {loading ? "Updating..." : "Update Password"}
                                </button>
                            </form>

                            {/* ✅ Feedback Messages */}
                            {error && <p className="error-msg">{error}</p>}
                            {success && <p className="success-msg">{success}</p>}

                            {/* ✅ Confirmation Modal */}
                            {showConfirm && (
                                <div className="modal-overlay">
                                    <div className="modal">
                                        <h3>Confirm Password Change</h3>
                                        <p>Are you sure you want to change your password?</p>
                                        <button className="login-submit-btn" onClick={handlePasswordUpdate} disabled={loading}>
                                            Yes, change it
                                        </button>
                                        <button className="login-submit-btn" onClick={() => setShowConfirm(false)}>Cancel</button>
                                    </div>
                                </div>
                            )}

                            {/* ✅ Basic Styling */}
                            <style>{`
                            .error-msg { color: red; margin-top: 10px; }
                            .success-msg { color: green; margin-top: 10px; }
                            .modal-overlay {
                                position: fixed; top: 0; left: 0; right: 0; bottom: 0;
                                background: rgba(0, 0, 0, 0.5);
                                display: flex; justify-content: center; align-items: center;
                                z-index: 999;
                            }
                            .modal {
                                background: white; padding: 20px; border-radius: 8px;
                                box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
                                max-width: 300px; text-align: center;
                            }
                            .modal button {
                                margin: 10px 5px;
                            }
                        `}</style>
                        </>
                    )}
                </section>
            )}

            {/* 4. Activity Logs (Placeholder if needed separately) */}
            <section className="profile-section">
                <h2
                    className="collapsible-header"
                    onClick={() => setShowScrubHistory(prev => !prev)}
                >
                    📄 Scrub History {showScrubHistory ? "▲ click to show less" : "▼ click to show more"}
                </h2>

                {scrubLogs.length === 0 ? (
                    <p>No Scrubs found.</p>
                ) : showScrubHistory ? (
                    scrubLogs.map(log => (
                        <div key={log.id} className="log-item">
                            <div onClick={() => toggleExpand(log.id)} className="log-header">
                                {log.date} – {log.xlsx_url.substring(log.xlsx_url.lastIndexOf("/") + 1)}
                            </div>
                            {expandedId === log.id && (
                                <div className="log-body">
                                    <p><strong>Total emails scrubbed:</strong> {log.total_emails_count}</p>
                                    <p><strong>Valid emails:</strong> {log.valid_emails_count}</p>
                                    <p><strong>Invalid emails:</strong> {log.invalid_emails_count}</p>
                                    <p><strong>Credits used:</strong> {log.scrub_credits_used}</p>
                                    {log.valid_emails_count > 0 && (
                                        <>
                                            <p>
                                                <strong>Download CSV:</strong>{" "}
                                                <button
                                                    type="button"
                                                    onClick={() => downloadFile(log.csv_url, token, log.csv_url.substring(log.csv_url.lastIndexOf("/") + 1))}
                                                    className="download-btn"
                                                >
                                                    {log.csv_url.substring(log.csv_url.lastIndexOf("/") + 1)}
                                                </button>
                                            </p>
                                            <p>
                                                <strong>Download XLSX:</strong>{" "}
                                                <button
                                                    type="button"
                                                    onClick={() => downloadFile(log.xlsx_url, token, log.xlsx_url.substring(log.xlsx_url.lastIndexOf("/") + 1))}
                                                    className="download-btn"
                                                >
                                                    {log.xlsx_url.substring(log.xlsx_url.lastIndexOf("/") + 1)}
                                                </button>
                                            </p>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    ))
                ) : (
                    <div className="log-item">
                        <div className="log-header">
                            {latestScrubLog.date} – {latestScrubLog.xlsx_url.substring(latestScrubLog.xlsx_url.lastIndexOf("/") + 1)}
                        </div>
                    </div>
                )}
            </section>

            {/* 3. Payment History */}
            <section className="profile-section">
                <h2
                    className="collapsible-header"
                    onClick={() => setShowPaymentHistory(prev => !prev)}
                >
                    💳 Payment History {showPaymentHistory ? "▲ click to show less" : "▼ click to show more" }
                </h2>
                {activityLogs.length === 0 ? (
                    <p>No purchases found.</p>
                ) : showPaymentHistory ? (
                    activityLogs.map(log => (
                        <div key={log.id} className="log-item">
                            <div onClick={() => toggleExpand(log.id)} className="log-header">
                                {log.date} – {log.purchase_type.toUpperCase()}
                            </div>
                            {expandedId === log.id && (
                                <div className="log-body">
                                    <p><strong>Amount:</strong> ${log.last_purchase_amount}</p>
                                    <p><strong>Type:</strong> {log.purchase_type}</p>
                                </div>
                            )}
                        </div>
                    ))
                ) : (
                    <div className="log-item">
                        <div className="log-header">
                            {latestPaymentLog.date} – {latestPaymentLog.purchase_type.toUpperCase()}
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}

export default Profile;